import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, Circle, HStack, Spinner } from '@chakra-ui/react';
import { Tooltip } from '@src/components/chakra-ui/tooltip';
import { useWsConnectionStatus } from '@src/hooks/useWsConnectionStatus';
import React from 'react';
export type ChatConnectionStatusProps = {
  boxProps?: BoxProps;
  /**
   * full — always show dot + label (e.g. chat nav footer)
   * compact — dot only; full text in tooltip
   * auto — hide label when the nearest size container is narrow
   *        (parent should set `containerType: 'inline-size'`)
   */
  density?: 'full' | 'compact' | 'auto';
};
function statusLabel(status: ReturnType<typeof useWsConnectionStatus>['status'], attempt: number, maxAttempts: number): string {
  if (status === 'reconnecting' && attempt > 0) {
    return `Reconnecting (${attempt}/${maxAttempts})...`;
  }
  if (status === 'connecting') {
    return 'Connecting...';
  }
  if (status === 'reconnecting') {
    return 'Reconnecting...';
  }
  if (status === 'offline') {
    return 'Offline';
  }
  return 'Connected';
}
export const ChatConnectionStatus: React.FC<ChatConnectionStatusProps> = props => {
  const $ = _c(27);
  const {
    density: t0
  } = props;
  const density = t0 === undefined ? "full" : t0;
  const {
    status,
    attempt,
    maxAttempts
  } = useWsConnectionStatus();
  let t1;
  if ($[0] !== attempt || $[1] !== maxAttempts || $[2] !== status) {
    t1 = statusLabel(status, attempt, maxAttempts);
    $[0] = attempt;
    $[1] = maxAttempts;
    $[2] = status;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const label = t1;
  let t2;
  if ($[4] !== status) {
    t2 = status === "connecting" || status === "reconnecting" ? <Spinner flexShrink={0} size="xs" /> : status === "offline" ? <Circle bgColor="orange" flexShrink={0} size="8px" /> : <Circle background="green.500" flexShrink={0} size="8px" />;
    $[4] = status;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const indicator = t2;
  let t3;
  if ($[6] !== density || $[7] !== label) {
    t3 = density === "compact" ? null : density === "full" ? <Box as="span" whiteSpace="nowrap">{label}</Box> : <Box as="span" css={{
      "@container (min-width: 30rem)": {
        display: "inline"
      }
    }} whiteSpace="nowrap" display="none">{label}</Box>;
    $[6] = density;
    $[7] = label;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const labelNode = t3;
  const t4 = density === "full" ? 2 : 1;
  const t5 = status === "connecting" || status === "reconnecting" ? 0.5 : 1;
  let t6;
  if ($[9] !== indicator || $[10] !== labelNode || $[11] !== t4 || $[12] !== t5) {
    t6 = <HStack align="center" flexShrink={0} gap={t4} justify="center" minWidth="8px" opacity={t5}>{indicator}{labelNode}</HStack>;
    $[9] = indicator;
    $[10] = labelNode;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const content = t6;
  if (density === "full") {
    let t7;
    if ($[14] !== content || $[15] !== props.boxProps) {
      t7 = <Box fontSize="10px" lineHeight={1} {...props.boxProps}>{content}</Box>;
      $[14] = content;
      $[15] = props.boxProps;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      placement: "top"
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== content || $[19] !== label) {
    t8 = <Box alignItems="center" aria-label={label} cursor="default" display="flex">{content}</Box>;
    $[18] = content;
    $[19] = label;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== label || $[22] !== t8) {
    t9 = <Tooltip content={label} openDelay={300} positioning={t7}>{t8}</Tooltip>;
    $[21] = label;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== props.boxProps || $[25] !== t9) {
    t10 = <Box fontSize="10px" lineHeight={1} {...props.boxProps}>{t9}</Box>;
    $[24] = props.boxProps;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
};